import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Undo2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar, UserLink } from "@/components/UserBits";

const PERIODS = [["today", "Heute"], ["week", "7 Tage"], ["month", "30 Tage"]];
const fmt = (s) => new Date(s).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });

function TopEarners({ onPick, picked }) {
  const [period, setPeriod] = useState("today");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/admin/points/top", { params: { period } }).then(({ data }) => setD(data)).catch((e) => toast.error(formatErr(e))); }, [period]);
  return (
    <div className="space-y-3" data-testid="audit-top">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-bold text-sm flex-1">Wer hat am meisten Punkte gesammelt?</p>
        {PERIODS.map(([k, l]) => <button key={k} onClick={() => setPeriod(k)} data-testid={`audit-period-${k}`} className={`px-3 h-8 rounded-full text-xs font-bold ${period === k ? "pink-gradient text-white" : "bg-rose-50 text-rose-600"}`}>{l}</button>)}
      </div>
      {!d ? <Loader2 className="w-5 h-5 animate-spin text-rose-500" /> : d.top.length === 0 ? <p className="text-sm text-slate-400">Keine Punkte in diesem Zeitraum.</p> : (
        <>
          <p className="text-xs text-slate-500">Durchschnitt der Top-Sammler: {d.average} Punkte. Rot markiert = mehr als 3× so viel.</p>
          <ul className="space-y-1.5 max-h-80 overflow-y-auto">
            {d.top.map((r, i) => (
              <li key={r.user.user_id}>
                <button onClick={() => onPick(r.user)} data-testid={`audit-top-${r.user.user_id}`} className={`w-full flex items-center gap-2 p-2 rounded-xl border text-left transition-colors ${picked === r.user.user_id ? "border-rose-400 bg-rose-50" : r.suspicious ? "border-red-200 bg-red-50/60" : "border-pink-100 hover:bg-rose-50/50"}`}>
                  <span className="w-5 text-xs font-bold text-slate-400">{i + 1}</span>
                  <UserAvatar userId={r.user.user_id} name={r.user.name} picture={r.user.picture} size="w-8 h-8" text="text-xs" />
                  <span className="flex-1 min-w-0"><span className="block text-sm font-semibold truncate">{r.user.name}{r.user.banned && " (gesperrt)"}</span><span className="block text-[11px] text-slate-400 truncate">{r.count}× · {r.kinds.join(", ")}</span></span>
                  {r.suspicious && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" data-testid={`audit-suspicious-${r.user.user_id}`} />}
                  <span className="text-sm font-extrabold text-rose-600 shrink-0">+{r.sum}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function Log({ user, onClear }) {
  const [kind, setKind] = useState("");
  const [d, setD] = useState(null);
  const [more, setMore] = useState(true);
  const load = useCallback(async (before) => {
    const { data } = await api.get("/admin/points/log", { params: { user_id: user?.user_id, kind: kind || undefined, before } });
    setMore(data.items.length === 100);
    setD((p) => ({ kinds: data.kinds, items: before && p ? [...p.items, ...data.items] : data.items }));
  }, [user, kind]);
  useEffect(() => { setD(null); load().catch((e) => toast.error(formatErr(e))); }, [load]);
  const revoke = async (t) => {
    if (!window.confirm(`${t.amount} Punkte von ${t.user.name} zurücknehmen?\n„${t.reason}“`)) return;
    try { await api.post(`/admin/points/${t.id}/revoke`); toast.success("Punkte zurückgenommen"); load(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="space-y-3" data-testid="audit-log">
      <div className="flex items-center gap-2 flex-wrap">
        <p className="font-bold text-sm flex-1">Alle Punkte-Aktivitäten{user && <> von <b>{user.name}</b></>}</p>
        {user && <button onClick={onClear} data-testid="audit-clear-user" className="inline-flex items-center gap-1 px-3 h-8 rounded-full bg-slate-100 text-xs font-semibold"><X className="w-3 h-3" /> Alle Nutzer</button>}
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="audit-kind" className="h-8 rounded-full border border-pink-200 px-2 text-xs bg-white">
          <option value="">Alle Arten</option>{d && Object.entries(d.kinds).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {!d ? <Loader2 className="w-5 h-5 animate-spin text-rose-500" /> : d.items.length === 0 ? <p className="text-sm text-slate-400">Keine Einträge.</p> : (
        <ul className="divide-y divide-pink-50 border border-pink-100 rounded-2xl overflow-hidden">
          {d.items.map((t) => (
            <li key={t.id} className={`flex items-center gap-2 px-3 py-2 text-sm ${t.revoked ? "opacity-50" : ""}`} data-testid={`audit-tx-${t.id}`}>
              <span className="flex-1 min-w-0">
                <span className="block truncate">{!user && <UserLink id={t.user.user_id} className="font-semibold">{t.user.name}</UserLink>}{!user && " · "}{t.reason}</span>
                <span className="block text-[11px] text-slate-400">{fmt(t.created_at)} · {t.kind_label}{t.revoked && " · zurückgenommen"}</span>
              </span>
              <span className={`shrink-0 font-bold ${t.amount > 0 ? "text-emerald-600" : "text-slate-500"}`}>{t.amount > 0 ? `+${t.amount}` : t.amount}</span>
              {t.amount > 0 && !t.revoked && <button onClick={() => revoke(t)} data-testid={`audit-revoke-${t.id}`} title="Zurücknehmen" aria-label="Zurücknehmen" className="shrink-0 p-1 text-slate-300 hover:text-red-500"><Undo2 className="w-4 h-4" /></button>}
            </li>
          ))}
        </ul>
      )}
      {d && more && <button onClick={() => load(d.items[d.items.length - 1].created_at)} data-testid="audit-more" className="text-sm font-semibold text-rose-600">Mehr laden</button>}
    </div>
  );
}

export function PointsAudit() {
  const [user, setUser] = useState(() => { const id = new URLSearchParams(window.location.search).get("user"); return id ? { user_id: id, name: "diesem Mitglied" } : null; });
  return (
    <div className="bg-white rounded-3xl p-6 border border-pink-100 space-y-6 max-w-3xl" data-testid="admin-points-audit">
      <div><h3 className="font-bold text-lg">Punkte-Prüfung</h3><p className="text-sm text-slate-500">Ab der unter „Punkte“ eingestellten Grenze (Standard 200 Punkte am Tag) bekommen ihr und alle Moderatoren automatisch eine Push-Warnung. Seht, wer wofür Punkte bekommen hat. Tippt auf ein Mitglied für alle Details. Unberechtigte Punkte nehmt ihr mit dem Pfeil zurück. Bei echtem Missbrauch könnt ihr das Mitglied unter „Mitglieder“ sperren.</p></div>
      <TopEarners onPick={setUser} picked={user?.user_id} />
      <Log user={user} onClear={() => setUser(null)} />
    </div>
  );
}
